/**
 * Input primitive.
 *
 * A labelled text input with error + disabled states. Wraps the native
 * `<input>` so feature screens can drop it in without re-implementing
 * label association, error messaging, and focus styles each time.
 *
 * Styling pulls from `var(--...)` custom properties emitted at app boot.
 * No hex, no raw px (CP5).
 *
 * Reveal (show/hide password): pass `reveal` on a `type="password"` field to
 * render an eye / eye-off toggle inside the input. The toggle is a real
 * `<button type="button">` (never submits the form), announces its state via
 * `aria-label` + `aria-pressed`, and flips the field between `password` and
 * `text`. Masked by default on mount. Every password field can opt in from
 * this one place; the field's own `autoComplete` is preserved unchanged.
 *
 * Legacy equivalent: inline `<input>` elements inside `.form-group` blocks
 * in `frontend/src/App.css` (lines 1629+). Label + input + error copy
 * are hand-written in every legacy screen; this primitive collapses the
 * pattern.
 */

import { forwardRef, useId, useState } from 'react';
import type { CSSProperties, InputHTMLAttributes, ReactNode } from 'react';
import { Icon } from './Icon';

export interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visible label. Required for a11y. Pass empty string + aria-label for bare inputs. */
  label: ReactNode;
  /** Error message. Presence switches input into error state. */
  error?: string;
  /** Supporting text under the input (only shown when no error). */
  hint?: string;
  /**
   * Show a show/hide toggle inside the field. Only takes effect on
   * `type="password"`; masked by default on mount. Opt in per password field.
   */
  reveal?: boolean;
  /** Test hook — mirrored onto the input; the reveal button gets `${id}-reveal`. */
  'data-testid'?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, id, disabled, style, type, reveal, 'data-testid': dataTestId, ...rest },
  ref
) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const describedById = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined;
  const hasError = Boolean(error);

  // Reveal only applies to a password field. `revealed` flips the rendered
  // input type between `password` and `text`; it starts masked on mount.
  const [revealed, setRevealed] = useState(false);
  const revealable = reveal === true && type === 'password';
  const effectiveType = revealable && revealed ? 'text' : type;

  const wrapperStyle: CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--space-1)',
    fontFamily: 'var(--font-family-sans)',
  };

  const labelStyle: CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-medium)',
    color: 'var(--color-text-secondary)',
  };

  const inputStyle: CSSProperties = {
    fontFamily: 'var(--font-family-sans)',
    fontSize: 'var(--font-size-base)',
    lineHeight: 'var(--line-height-normal)',
    color: 'var(--color-text-primary)',
    backgroundColor: 'var(--color-bg-primary)',
    borderWidth: '0.0625rem', // hairline — rem keeps CP5 (no raw pixel literal in source)
    borderStyle: 'solid',
    borderColor: hasError ? 'var(--color-status-danger)' : 'var(--color-border-strong)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-2) var(--space-3)',
    // Fill the field wrapper so the absolutely-positioned reveal button (pinned
    // to the wrapper's trailing edge) lands INSIDE the input box, not past its
    // right border. Without this the input shrinks to its intrinsic width and
    // the eye floats outside the field.
    width: revealable ? '100%' : undefined,
    // Reserve room on the trailing edge for the reveal button + its inset so
    // typed text never slides under it.
    paddingRight: revealable ? 'var(--space-12)' : undefined,
    opacity: disabled ? 0.6 : 1,
    cursor: disabled ? 'not-allowed' : 'text',
    ...style,
  };

  const messageStyle: CSSProperties = {
    fontSize: 'var(--font-size-xs)',
    color: hasError ? 'var(--color-status-danger)' : 'var(--color-text-muted)',
  };

  // Wrapper around the input so the reveal button can sit inside the field.
  const fieldStyle: CSSProperties = { position: 'relative', display: 'flex' };

  // The toggle is a full ≥2.75rem square tap target pinned to the trailing
  // edge, vertically centered, tokens-only.
  const revealButtonStyle: CSSProperties = {
    position: 'absolute',
    top: '50%',
    // Inset from the trailing border so the eye reads as sitting inside the
    // field, not crammed against the edge.
    right: 'var(--space-1)',
    transform: 'translateY(-50%)',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '2.75rem',
    height: '2.75rem',
    padding: 0,
    background: 'transparent',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    color: 'var(--color-text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 'var(--font-size-lg)',
  };

  const input = (
    <input
      {...rest}
      type={effectiveType}
      id={inputId}
      ref={ref}
      disabled={disabled}
      data-testid={dataTestId}
      aria-invalid={hasError || undefined}
      aria-describedby={describedById}
      style={inputStyle}
    />
  );

  return (
    <div style={wrapperStyle} data-has-error={hasError || undefined}>
      <label htmlFor={inputId} style={labelStyle}>
        {label}
      </label>
      {revealable ? (
        <div style={fieldStyle}>
          {input}
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            disabled={disabled}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            aria-controls={inputId}
            title={revealed ? 'Hide password' : 'Show password'}
            style={revealButtonStyle}
            data-testid={dataTestId ? `${dataTestId}-reveal` : 'input-reveal'}
          >
            <Icon name={revealed ? 'eye-off' : 'eye'} />
          </button>
        </div>
      ) : (
        input
      )}
      {error ? (
        <span id={`${inputId}-error`} role="alert" style={messageStyle}>
          {error}
        </span>
      ) : hint ? (
        <span id={`${inputId}-hint`} style={messageStyle}>
          {hint}
        </span>
      ) : null}
    </div>
  );
});
